import React from 'react';

import cx from 'classnames';
import { AirplaneInFlight, Path } from 'phosphor-react';
import { isEmpty } from 'ramda';
import { useFormContext } from 'react-hook-form';

import { HookCheckbox } from 'components/shared/HookFormComponents';
import { TabLabel } from 'components/shared/TabSelector';
import { ACCOUNT_TYPE_AGENT } from 'constants/index';
import { FLIGHTS } from 'constants/texts';
import { EOrganizedBy, IApiFlightTripEvent } from 'detailsv2/structure';
import { PERMISSION_GUEST_PORTAL, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';

import cs from '../../../../../index.module.css';
import { FlightAPIOptions } from '../../../FlightAPIOption/types';
import { FlightSearchLock } from '../../../SearchBy/FlightSearchLock';
import { SearchByAirports } from '../../../SearchBy/SearchByAirports';
import { SearchByFlightNumber } from '../../../SearchBy/SearchByFlightNumber';
import { SearchResults } from '../../../SearchBy/SearchResults';
import { FlightTab } from '../../../types';
import s from '../../FlightTabs.module.css';
import { Content } from '../Content';
import { useFlightApi } from './useFlightApi';

type ContentProps = {
  tripEvent: IApiFlightTripEvent;
  activeItems: FlightAPIOptions;
  setActiveItems: React.Dispatch<React.SetStateAction<FlightAPIOptions>>;
  activeTab: FlightTab;
  setActiveTab: (tab: FlightTab) => void;
  isDisabled: boolean;
  organizedBy: EOrganizedBy;
};

export const FlightNumberTab = () => <TabLabel Icon={AirplaneInFlight} text="Flight Number" />;
export const FlightRouteTab = () => <TabLabel Icon={Path} text="Flight Route" />;

export const FlightApiContent = (props: ContentProps) => {
  const methods = useFormContext();
  const { control } = methods;
  const flightApi = useFlightApi({
    methods,
    tripEvent: props.tripEvent,
    organizedBy: props.organizedBy,
  });
  const isTier2Subscription = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, PERMISSION_GUEST_PORTAL);

  return (
    <Content organizedBy={props.organizedBy}>
      <div className={cx(s.lockContainer, !isTier2Subscription && s.notSubscriptionPaddings)}>
        {!isTier2Subscription && props.activeTab !== FlightTab.MANUAL && (
          <FlightSearchLock onClick={() => props.setActiveTab(FlightTab.MANUAL)} />
        )}

        <div>
          {props.activeTab === FlightTab.NUMBER_SEARCH ? (
            <SearchByFlightNumber
              tripEvent={props.tripEvent}
              organizedBy={props.organizedBy}
              hasSelectedValues={!isEmpty(props.activeItems)}
              setSearchResults={flightApi.setSearchResults}
              setActiveItems={props.setActiveItems}
              setShowSelect={flightApi.setShowSelect}
            />
          ) : (
            <SearchByAirports
              tripEvent={props.tripEvent}
              organizedBy={props.organizedBy}
              hasSelectedValues={!isEmpty(props.activeItems)}
              setSearchResults={flightApi.setSearchResults}
              setActiveItems={props.setActiveItems}
              setShowSelect={flightApi.setShowSelect}
            />
          )}
        </div>

        <div className={s.notifications}>
          <HookCheckbox name="data.notifyAgent" control={control} className={cs.checkboxText} defaultValue={true}>
            {FLIGHTS.CHECKBOXES.NOTIFY}
          </HookCheckbox>
          <HookCheckbox name="data.updateClientSide" control={control} className={cs.checkboxText} defaultValue={true}>
            {FLIGHTS.CHECKBOXES.UPDATE}
          </HookCheckbox>
        </div>

        <div id="addManually" />
        {!props.isDisabled && (
          <SearchResults
            methods={methods}
            activeItems={props.activeItems}
            setActiveItems={props.setActiveItems}
            searchResults={flightApi.searchResults}
            showSelect={flightApi.showSelect}
            isNoData={flightApi.isNoData}
          />
        )}
      </div>
    </Content>
  );
};
